import { useEffect, useRef, useState } from 'react';
import type { ChatMessage } from '@hypevault/shared-types';

/**
 * Chat Highlight — v2 port of v1's chat-highlight.ejs.
 * Pops a chat message in big text when it matches one of the configured triggers.
 *
 * Config (all optional):
 *   displayDuration: seconds                  (default 8)
 *   accentColor: string                       (default '#a78bfa')
 *   highlightCommand: string                  (default '!highlight' — anyone with mod/broadcaster badge can trigger)
 *   highlightModeratorsOnly: boolean          (default true — restricts highlightCommand to mods)
 *   keywords: string[]                        (case-insensitive substring match; default [])
 *   badges: ChatBadge[]                       (auto-highlight messages from these badges; default [])
 *   minMessageLength: number                  (default 0)
 *   fontSize: number                          (default 36)
 */

interface Highlight {
  id: string;
  message: ChatMessage;
}

export function ChatHighlightWidget({
  fields,
  latestChatMessage,
}: {
  fields: Record<string, unknown>;
  latestChatMessage: ChatMessage | null;
}) {
  const displayDuration = Number(fields.displayDuration ?? 8) * 1000;
  const accent = String(fields.accentColor ?? '#a78bfa');
  const highlightCommand = String(fields.highlightCommand ?? 'highlight').toLowerCase();
  const modsOnly = (fields.highlightModeratorsOnly as boolean | undefined) ?? true;
  const keywords = (fields.keywords as string[] | undefined) ?? [];
  const triggerBadges = (fields.badges as string[] | undefined) ?? [];
  const minLength = Number(fields.minMessageLength ?? 0);
  const fontSize = Number(fields.fontSize ?? 36);

  const [current, setCurrent] = useState<Highlight | null>(null);
  const [phase, setPhase] = useState<'in' | 'out'>('in');
  const lastId = useRef<string | null>(null);

  useEffect(() => {
    if (!latestChatMessage || latestChatMessage.id === lastId.current) return;
    lastId.current = latestChatMessage.id;

    let shouldHighlight = false;
    let actualMessage = latestChatMessage.message;

    // Command-based highlight: !highlight <message>
    if (latestChatMessage.command === highlightCommand) {
      const canTrigger =
        !modsOnly ||
        latestChatMessage.badges.includes('broadcaster') ||
        latestChatMessage.badges.includes('mod');
      if (canTrigger) {
        shouldHighlight = true;
        actualMessage = latestChatMessage.args.join(' ');
      }
    }

    // Badge-based auto highlight
    if (
      !shouldHighlight &&
      triggerBadges.length > 0 &&
      latestChatMessage.badges.some((b) => triggerBadges.includes(b))
    ) {
      shouldHighlight = true;
    }

    // Keyword-based auto highlight
    if (!shouldHighlight && keywords.length > 0) {
      const lower = latestChatMessage.message.toLowerCase();
      if (keywords.some((k) => lower.includes(k.toLowerCase()))) {
        shouldHighlight = true;
      }
    }

    if (!shouldHighlight) return;
    if (actualMessage.trim().length < minLength) return;

    const id = latestChatMessage.id;
    setCurrent({
      id,
      message: { ...latestChatMessage, message: actualMessage, command: null, args: [] },
    });
    setPhase('in');
    const tOut = setTimeout(() => setPhase('out'), displayDuration - 400);
    const tEnd = setTimeout(() => setCurrent((c) => (c?.id === id ? null : c)), displayDuration);
    return () => {
      clearTimeout(tOut);
      clearTimeout(tEnd);
    };
  }, [
    latestChatMessage,
    highlightCommand,
    modsOnly,
    keywords,
    triggerBadges,
    minLength,
    displayDuration,
  ]);

  if (!current) return null;

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Inter', system-ui, sans-serif",
        color: 'white',
        opacity: phase === 'in' ? 1 : 0,
        transform: phase === 'in' ? 'scale(1)' : 'scale(0.96)',
        transition: 'opacity 400ms ease, transform 400ms ease',
      }}
    >
      <div
        style={{
          maxWidth: '85%',
          background: 'linear-gradient(135deg, rgba(15,12,30,0.93), rgba(22,16,46,0.93))',
          border: `2px solid ${accent}80`,
          borderRadius: 18,
          padding: '24px 32px',
          boxShadow: `0 12px 48px rgba(0,0,0,0.6), 0 0 64px ${accent}40`,
          textAlign: 'center',
          position: 'relative',
          animation: 'ch-in 0.5s cubic-bezier(0.16,1,0.3,1) both',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 28,
            right: 28,
            height: 2,
            background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
          }}
        />
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 2,
            color: accent,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          ★ Highlight
        </div>
        <div
          style={{
            fontSize,
            fontWeight: 800,
            lineHeight: 1.25,
            marginBottom: 8,
            wordBreak: 'break-word',
          }}
        >
          {current.message.message}
        </div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'rgba(255,255,255,0.6)',
          }}
        >
          — {current.message.viewerName}
        </div>
      </div>
      <style>{`
        @keyframes ch-in {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
